import { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useStore } from './store';
import { authApi, templatesApi, cloudApi } from './services/api';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import OAuthCallback from './pages/OAuthCallback';
import SubscribeNow from './pages/SubscribeNow';

// Components
import Notifications from './components/Notifications';
import LoadingScreen from './components/LoadingScreen';
import SubscriptionGate from './components/SubscriptionGate';
import SubscriptionModal from './components/SubscriptionModal';

function ProtectedRoute({ children, authChecked }: { children: React.ReactNode; authChecked: boolean }) {
  const { isAuthenticated } = useStore();
  const location = useLocation();

  if (!authChecked) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

// Convert hex color to space-separated RGB channels for Tailwind
function hexToRgbChannels(hex: string): string {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = (num >> 16) & 0xff;
  const g = (num >> 8) & 0xff;
  const b = num & 0xff;
  return `${r} ${g} ${b}`;
}

function adjustChannels(hex: string, amount: number): string {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = Math.min(255, Math.max(0, ((num >> 16) & 0xff) + amount));
  const g = Math.min(255, Math.max(0, ((num >> 8) & 0xff) + amount));
  const b = Math.min(255, Math.max(0, (num & 0xff) + amount));
  return `${r} ${g} ${b}`;
}

function App() {
  const {
    setUser,
    setSubscription,
    setAllTemplates,
    setDocumentTemplates,
    setCloudConnections,
    isAuthenticated,
    brandingSettings,
  } = useStore();

  const [authChecked, setAuthChecked] = useState(false);

  // Apply branding settings as CSS custom properties
  useEffect(() => {
    const root = document.documentElement;
    const color = brandingSettings.accentColor || '#3b4e6d';
    root.style.setProperty('--accent-rgb', hexToRgbChannels(color));
    root.style.setProperty('--accent-light-rgb', adjustChannels(color, 20));
    root.style.setProperty('--accent-dark-rgb', adjustChannels(color, -20));
    root.style.setProperty('--accent-text-color', brandingSettings.accentTextColor || '#ffffff');
  }, [brandingSettings.accentColor, brandingSettings.accentTextColor]);

  // Check authentication on mount via httpOnly cookie
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Cookie is sent automatically with the request
        const { user, subscription } = await authApi.getMe();
        setUser(user);
        setSubscription(subscription);
      } catch {
        // Cookie invalid or expired - user will be redirected to login
      } finally {
        setAuthChecked(true);
      }
    };

    checkAuth();
  }, [setUser, setSubscription]);

  // Load templates and cloud status when authenticated
  useEffect(() => {
    if (!isAuthenticated) return;

    const loadData = async () => {
      try {
        // Load templates
        const [letterTemplates, docTemplates] = await Promise.all([
          templatesApi.getAll(),
          templatesApi.getAllDocumentTemplates(),
        ]);
        setAllTemplates(letterTemplates);
        setDocumentTemplates(docTemplates);

        // Load cloud connection status
        const cloudStatus = await cloudApi.getStatus();
        setCloudConnections(cloudStatus);
      } catch (error) {
        console.error('Failed to load initial data:', error);
      }
    };

    loadData();
  }, [isAuthenticated, setAllTemplates, setDocumentTemplates, setCloudConnections]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Notifications />
      <SubscriptionModal />

      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />

        {/* Subscribe page - requires auth but not subscription */}
        <Route
          path="/subscribe"
          element={
            <ProtectedRoute authChecked={authChecked}>
              <SubscribeNow />
            </ProtectedRoute>
          }
        />

        {/* OAuth callbacks */}
        <Route path="/auth/google/callback" element={<OAuthCallback provider="google" />} />
        <Route path="/auth/dropbox/callback" element={<OAuthCallback provider="dropbox" />} />
        <Route path="/auth/onedrive/callback" element={<OAuthCallback provider="onedrive" />} />

        {/* Protected routes - require valid subscription */}
        <Route
          path="/*"
          element={
            <ProtectedRoute authChecked={authChecked}>
              <SubscriptionGate>
                <Dashboard />
              </SubscriptionGate>
            </ProtectedRoute>
          }
        />
      </Routes>
    </div>
  );
}

export default App;
